/* useVisitorCall — Scan2Call visitor-side call lifecycle
   ------------------------------------------------------
   Extracted from `pages/DoorbellVisitor.js` (Jul 2026) to keep the page
   component focused on rendering while the call/voicemail state machine
   lives here and can be unit-tested + reused (e.g. from an embedded
   widget on the marketing site if we ever want it).

   Responsibilities:
     • Agora RTC session — join, publish visitor mic + camera, subscribe
       to the resident's audio (always) and video (optional PIP).
     • Ring lifecycle stages:
         idle → ringing → connected
               ↘ (60s no answer) → voicemail_prompt → voicemail_recording
                                                   → voicemail_sending
                                                   → voicemail_sent
         any → ended
     • Voicemail MediaRecorder — TTS prompt + beep + record up to 30 s +
       upload as base64 to `/api/doorbell/ring/:id/voicemail`.
     • Timers — ringing, connected, voicemail elapsed.
     • Cleanup on unmount + on tab-hidden while recording.

   The hook is intentionally DUMB about UI. It exposes `stage` and the
   timer values; the page component decides how to render them.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import AgoraRTC from "agora-rtc-sdk-ng";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const NO_ANSWER_S = 60;         // matches NO_ANSWER_VOICEMAIL_S in backend
export const VOICEMAIL_MAX_S = 30;     // matches VOICEMAIL_MAX_S in backend
const VOICEMAIL_PROMPT = "Resident unavailable, please leave a voice note after the beep.";

// ---- helpers ----------------------------------------------------------

function pickMimeType() {
  // Prefer opus/webm (small + universally decodable), then mp4 (Safari).
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const m of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return "";
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Strip the `data:<mime>;base64,` prefix.
      const s = reader.result || "";
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function playBeep() {
  // 880 Hz square-ish ping for ~250 ms — closest thing to "leave a message
  // after the tone" the average user expects, without bundling an audio
  // asset. Wrapped in try/catch because some browsers gate audio context
  // creation behind a user gesture; visitor clicked "Ring" so this is OK.
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.0001;
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
    return new Promise((r) => setTimeout(() => { try { ctx.close(); } catch { /* noop */ } r(); }, 400));
  } catch { /* ignore — audio unlock races are non-fatal */ }
}

function speak(text) {
  // Wraps speechSynthesis in a promise so we can sequence TTS → beep →
  // record cleanly. Resolves immediately if synthesis isn't available.
  return new Promise((resolve) => {
    try {
      if (!("speechSynthesis" in window)) return resolve();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0; u.pitch = 1.0; u.volume = 1.0;
      u.onend = () => resolve();
      u.onerror = () => resolve();
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch { resolve(); }
  });
}

// ---- hook -------------------------------------------------------------

/**
 * @param {{ qrToken: string, visitorName?: string }} opts
 * @returns {object} call state + control methods for the visitor page
 */
export function useVisitorCall({ qrToken, visitorName = "" }) {
  // stage: idle | ringing | connected | voicemail_prompt | voicemail_recording | voicemail_sending | voicemail_sent | ended
  const [stage, setStage] = useState("idle");
  const [ring, setRing] = useState(null);
  const [callError, setCallError] = useState(null);
  const [secsRinging, setSecsRinging] = useState(0);
  const [secsConnected, setSecsConnected] = useState(0);
  const [vmSecs, setVmSecs] = useState(0);
  // True while the resident's video track is live — drives the resident
  // PIP vs. the "voice only" pill on the visitor page. (Restored Jul
  // 2026 — the resident publishes camera+mic upfront again, see
  // useResidentCall.js header.)
  const [residentVideoOn, setResidentVideoOn] = useState(false);
  // Agora network quality (worst of up/down; 0 unknown, 1 best … 6 down).
  const [netQuality, setNetQuality] = useState(0);

  const clientRef = useRef(null);
  const camRef = useRef(null);
  const micRef = useRef(null);
  const previewRef = useRef(null);
  const residentContainerRef = useRef(null);
  const residentVideoTrackRef = useRef(null);

  // Callback ref for the resident-video PIP. iOS Safari can mount the
  // PIP a beat after the track arrives; playing from whichever side is
  // ready avoids the black-PIP race.
  const attachResidentContainer = useCallback((el) => {
    residentContainerRef.current = el;
    if (el && residentVideoTrackRef.current) {
      try { residentVideoTrackRef.current.play(el); } catch { /* noop — track disposed */ }
    }
  }, []);

  // Voicemail-specific refs. Kept separate from the Agora mic above so
  // the visitor's audio stream is *not* reused — we cleanly disconnect
  // from Agora before starting the recording.
  const vmStreamRef = useRef(null);
  const vmRecorderRef = useRef(null);
  const vmChunksRef = useRef([]);
  const vmTimerRef = useRef(null);
  const vmStageRef = useRef("idle"); // mirror of `stage` for use inside async callbacks

  // ---- cleanup --------------------------------------------------------

  const cleanupAgora = useCallback(async () => {
    try { if (camRef.current) { camRef.current.stop(); camRef.current.close(); camRef.current = null; } } catch { /* noop */ }
    try { if (micRef.current) { micRef.current.stop(); micRef.current.close(); micRef.current = null; } } catch { /* noop */ }
    try { if (residentVideoTrackRef.current) { residentVideoTrackRef.current.stop(); residentVideoTrackRef.current = null; } } catch { /* noop */ }
    setResidentVideoOn(false);
    try { if (clientRef.current) { await clientRef.current.leave(); clientRef.current = null; } } catch { /* noop */ }
  }, []);

  const cleanupVoicemail = useCallback(() => {
    try { if (vmTimerRef.current) { clearInterval(vmTimerRef.current); vmTimerRef.current = null; } } catch { /* noop */ }
    try { if (vmRecorderRef.current && vmRecorderRef.current.state !== "inactive") vmRecorderRef.current.stop(); } catch { /* noop */ }
    try { if (vmStreamRef.current) vmStreamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    vmRecorderRef.current = null;
    vmStreamRef.current = null;
    vmChunksRef.current = [];
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
  }, []);

  const cleanupAll = useCallback(async () => {
    await cleanupAgora();
    cleanupVoicemail();
  }, [cleanupAgora, cleanupVoicemail]);

  // ---- ring lifecycle -------------------------------------------------

  const handleRing = useCallback(async () => {
    setCallError(null);
    setStage("ringing");
    vmStageRef.current = "ringing";
    // Best-effort GPS grab so the resident sees "Calling from <precise
    // address>" rather than just the IP-derived city. Timeout after 3s
    // so a slow GPS fix doesn't delay the ring — we fall back to IP
    // geolocation on the server side.
    const getPos = () => new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      let done = false;
      const finish = (v) => { if (!done) { done = true; resolve(v); } };
      navigator.geolocation.getCurrentPosition(
        (p) => finish({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
        () => finish(null),
        { enableHighAccuracy: false, timeout: 3000, maximumAge: 60000 },
      );
      setTimeout(() => finish(null), 3200);
    });
    const pos = await getPos();
    let r;
    try {
      r = await axios.post(`${API}/doorbell/ring/${qrToken}`, {
        visitor_name: (visitorName || "").trim() || null,
        latitude: pos?.latitude ?? null,
        longitude: pos?.longitude ?? null,
      });
    } catch (e) {
      setCallError(e?.response?.data?.detail || "Could not place the call.");
      setStage("ended");
      vmStageRef.current = "ended";
      return;
    }
    setRing(r.data);
    try {
      // codec: "h264" — must match the resident's codec choice. VP8
      // has documented negotiation failures on iOS Safari 15+ where
      // mid-call track publishes are silently dropped.
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "h264" });
      clientRef.current = client;
      // Subscribe to the resident's audio AND video. The resident now
      // publishes camera+mic upfront (see useResidentCall.js header);
      // video may also drop/return mid-call when they toggle their
      // camera (setMuted fires user-unpublished/republished here).
      client.on("user-published", async (remoteUser, mediaType) => {
        try {
          await client.subscribe(remoteUser, mediaType);
        } catch (subErr) {

          console.error(`[Scan2Call visitor] subscribe(${mediaType}) failed:`, subErr);
          return;
        }
        if (mediaType === "audio") {
          try { remoteUser.audioTrack.play(); } catch (e) {
            console.warn("[Scan2Call visitor] audio play failed", e);
          }
          setStage("connected");
          vmStageRef.current = "connected";
        }
        if (mediaType === "video") {
          residentVideoTrackRef.current = remoteUser.videoTrack;
          setResidentVideoOn(true);
          if (residentContainerRef.current) {
            try { remoteUser.videoTrack.play(residentContainerRef.current); } catch (e) {
              console.warn("[Scan2Call visitor] resident video play failed", e);
            }
          }
        }
      });
      client.on("user-unpublished", (remoteUser, mediaType) => {
        // Resident toggled camera off (setMuted) or hung up — WS ends
        // the call on hangup; here we just drop the PIP.
        if (mediaType === "video") {
          try { residentVideoTrackRef.current?.stop(); } catch { /* noop */ }
          residentVideoTrackRef.current = null;
          setResidentVideoOn(false);
        }
      });
      // The resident left the channel entirely — they hung up on their
      // side (or switched to another waiting call). The visitor has no
      // WebSocket, so this Agora event is the only reliable signal to
      // close out the call UI instead of leaving a frozen frame.
      client.on("user-left", () => {
        if (vmStageRef.current !== "connected") return;
        vmStageRef.current = "ended";
        setStage("ended");
        cleanupAgora();
      });
      client.on("network-quality", (q) => {
        setNetQuality(Math.max(q?.uplinkNetworkQuality || 0, q?.downlinkNetworkQuality || 0));
      });
      await client.join(r.data.agora_app_id, r.data.channel, r.data.visitor_token, r.data.visitor_uid);
      const [mic, cam] = await Promise.all([
        AgoraRTC.createMicrophoneAudioTrack(),
        AgoraRTC.createCameraVideoTrack(),
      ]);
      micRef.current = mic;
      camRef.current = cam;
      if (previewRef.current) cam.play(previewRef.current);
      await client.publish([mic, cam]);
    } catch (e) {
      setCallError(`Camera/mic error: ${e.message || e}`);
      await cleanupAll();
      setStage("ended");
      vmStageRef.current = "ended";
    }
  }, [qrToken, visitorName, cleanupAll, cleanupAgora]);

  const handleHangup = useCallback(async (skipEndCall) => {
    if (ring && !skipEndCall) {
      try { await axios.post(`${API}/doorbell/ring/${ring.ring_id}/end`); } catch { /* noop — backend reconciler will handle it */ }
    }
    await cleanupAll();
    setStage("ended");
    vmStageRef.current = "ended";
  }, [ring, cleanupAll]);

  // ---- voicemail lifecycle -------------------------------------------

  const uploadVoicemail = useCallback(async (durationS) => {
    if (vmStageRef.current === "voicemail_sending" || vmStageRef.current === "voicemail_sent") return;
    vmStageRef.current = "voicemail_sending";
    setStage("voicemail_sending");
    const chunks = vmChunksRef.current;
    if (!ring || chunks.length === 0) {
      setStage("ended");
      vmStageRef.current = "ended";
      return;
    }
    const mime = vmRecorderRef.current?.mimeType || pickMimeType() || "audio/webm";
    const blob = new Blob(chunks, { type: mime });
    try {
      const b64 = await blobToBase64(blob);
      await axios.post(`${API}/doorbell/ring/${ring.ring_id}/voicemail`, {
        audio_b64: b64,
        mime_type: mime,
        duration_s: Math.max(1, Math.round(durationS)),
      });
      setStage("voicemail_sent");
      vmStageRef.current = "voicemail_sent";
    } catch (e) {
      // Don't strand the visitor on a failed upload — surface the error
      // but still let them try again with a "Ring again" button.
      setCallError(e?.response?.data?.detail || "Could not send voicemail.");
      setStage("ended");
      vmStageRef.current = "ended";
    } finally {
      cleanupVoicemail();
    }
  }, [ring, cleanupVoicemail]);

  const startVoicemailRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      vmStreamRef.current = stream;
      const mime = pickMimeType();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      vmRecorderRef.current = rec;
      vmChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) vmChunksRef.current.push(e.data); };
      // onstop happens *after* the final ondataavailable, so we can safely
      // package the chunks here.
      rec.onstop = () => {
        const start = rec._guardStart || Date.now();
        const finalDuration = Math.min(VOICEMAIL_MAX_S, Math.round((Date.now() - start) / 1000));
        uploadVoicemail(finalDuration || 1);
      };
      rec._guardStart = Date.now();
      rec.start(250); // emit chunks every 250ms — keeps memory bounded
      vmStageRef.current = "voicemail_recording";
      setStage("voicemail_recording");
      setVmSecs(0);
      vmTimerRef.current = setInterval(() => {
        setVmSecs((s) => {
          const nx = s + 1;
          if (nx >= VOICEMAIL_MAX_S) {
            try { rec.state !== "inactive" && rec.stop(); } catch { /* noop */ }
            clearInterval(vmTimerRef.current);
            vmTimerRef.current = null;
          }
          return nx;
        });
      }, 1000);
    } catch (e) {
      setCallError(`Microphone error: ${e.message || e}. Voicemail not sent.`);
      vmStageRef.current = "ended";
      setStage("ended");
    }
  }, [uploadVoicemail]);

  const enterVoicemail = useCallback(async () => {
    // RESILIENCE — Page Visibility guard. If the visitor closed/tabbed
    // away from the page by the time the no-answer timer fires, we DON'T
    // want to silently record 30 s of their background audio. Hang up
    // cleanly instead; the backend reconciler marks the ring as `missed`.
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      await handleHangup();
      return;
    }
    vmStageRef.current = "voicemail_prompt";
    setStage("voicemail_prompt");
    // Hard-cut from Agora so the resident's UI ring dialog clears and
    // the visitor's mic is freed for MediaRecorder.
    await cleanupAgora();
    await speak(VOICEMAIL_PROMPT);
    await playBeep();
    // Re-check visibility right before we open MediaRecorder — a ~1-2 s
    // TTS + beep window is enough for the visitor to switch tabs.
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      cleanupVoicemail();
      await handleHangup();
      return;
    }
    await startVoicemailRecording();
  }, [cleanupAgora, cleanupVoicemail, handleHangup, startVoicemailRecording]);

  const stopRecording = useCallback(() => {
    try {
      if (vmRecorderRef.current && vmRecorderRef.current.state !== "inactive") {
        vmRecorderRef.current.stop();
      }
    } catch { /* noop */ }
    if (vmTimerRef.current) { clearInterval(vmTimerRef.current); vmTimerRef.current = null; }
  }, []);

  const cancelVoicemail = useCallback(async () => {
    cleanupVoicemail();
    await handleHangup();
  }, [cleanupVoicemail, handleHangup]);

  // ---- timers ---------------------------------------------------------

  // Ringing timer — after NO_ANSWER_S, switch to voicemail mode.
  useEffect(() => {
    if (stage !== "ringing") return undefined;
    setSecsRinging(0);
    const id = setInterval(() => {
      setSecsRinging((s) => {
        if (s + 1 >= NO_ANSWER_S) {
          clearInterval(id);
          enterVoicemail();
          return NO_ANSWER_S;
        }
        return s + 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [stage, enterVoicemail]);

  // Connected timer — calls have NO upper time limit; the backend's
  // billing watchdog force-ends when the resident's wallet runs out.
  useEffect(() => {
    if (stage !== "connected") return undefined;
    setSecsConnected(0);
    const id = setInterval(() => setSecsConnected((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [stage]);

  // Unmount safety — always tear down Agora + voicemail on unmount.
  useEffect(() => () => { cleanupAll(); }, [cleanupAll]);

  // Page Visibility — abort any voicemail recording if the visitor
  // backgrounds the tab (screen lock / app switch on mobile).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") return;
      if (vmStageRef.current === "voicemail_recording" || vmStageRef.current === "voicemail_prompt") {
        cleanupVoicemail();
        handleHangup().catch(() => { /* noop — reconciler will handle it */ });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [cleanupVoicemail, handleHangup]);

  // ---- external reset -------------------------------------------------

  // Resets the hook's internal state so a "Call again" button can put
  // the visitor back into `idle` and let the page re-trigger a ring.
  // Does NOT itself re-fire the ring — the page decides that.
  const reset = useCallback(() => {
    setStage("idle");
    setRing(null);
    setCallError(null);
    setSecsRinging(0);
    setSecsConnected(0);
    setVmSecs(0);
    vmStageRef.current = "idle";
  }, []);

  return {
    // state
    stage,
    ring,
    callError,
    secsRinging,
    secsConnected,
    vmSecs,
    residentVideoOn,
    netQuality,
    // refs — page attaches these to the DOM
    previewRef,
    attachResidentContainer,
    // actions
    handleRing,
    handleHangup,
    stopRecording,
    cancelVoicemail,
    reset,
  };
}
